"use client";
import { useEffect, useState } from "react";
import RatingStarDisplay from "@components/RatingSelector/RatingStarDisplay";
import styles from "./BookDealsCardNew.module.css";
import NextImage from "@components/Image/NextImage";
import {
  amazonAsinParser,
  BuildServerlessBookCoverImageUrl,
  generateAmazonBookPreviewLink,
  isFutureDate,
  formatPrice
} from "helpers/Helper";
import IMAGESIZES from "src/constants/IMAGESIZES";
import ReadMoreLess from "@components/ReadMoreLess/ReadMoreLess";
import Post from "src/constants/Post";

const BookDealsCardNew = ({ deal, index, bannerInfo, selectedRetailers, postType, showRetailerForPrice = false }) => {
  const [imageSize, setImageSize] = useState({ width: 180, height: 246 });
  const renderComingSoonBanner = isFutureDate(deal?.pub_date);
  const displayFreeVoiceBanner = deal.freevoice;
  const isPromotedToKindleDeals = deal.isPromotedToKindleDeals;
  useEffect(() => {
    const updateSize = () => {
      if (window.innerWidth <= 768) {
        setImageSize({ width: 110, height: 190 });
      } else {
        setImageSize({ width: 180, height: 246 });
      }
    };

    updateSize(); // Set initial size
    window.addEventListener("resize", updateSize);

    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const renderBookImage = (title, image, amazonLink) => {
    let bookCoverUrl = BuildServerlessBookCoverImageUrl(
      IMAGESIZES.BC_DEALSPAGE,
      image
    );

    const previewLinkInner = (
      <span className={styles.readPreview}>
        <NextImage
          ImageUrl={`${process.env.S3_IMAGE_URL}/eye.svg`}
          Title="Read Preview"
          Alt_Text="Read Preview"
          className=""
          width={16}
          height={16}
          style={{ verticalAlign: "middle" }}
          isLazyload={true}
        />
        <span className={styles.previewLink}>READ PREVIEW</span>
      </span>
    );

    return (
      <div className={styles.bookImage}>
        {renderAmazonPreviewLink(
          bookCoverUrl,
          previewLinkInner,
          styles.previewLink,
          amazonLink,
          title
        )}
      </div>
    );
  };

  const renderAmazonPreviewLink = (
    bookCoverUrl,
    innerContent,
    className,
    amazonLink,
    title,
    renderPreview = true
  ) => {
    let previewLink = "",
      asin = "";

    if (amazonLink) {
      previewLink = generateAmazonBookPreviewLink(amazonLink);
      asin = amazonAsinParser(amazonLink);
    }

    const bookImageHTML = (
      <NextImage
        className={styles.bookImage}
        width={imageSize.width}
        height={imageSize.height}
        ImageUrl={bookCoverUrl}
        Title={title}
        Alt_Text={`Buy ${title} at Amazon`}
        isLazyload={true}
      />
    );

    const previewLinkHTML = (
      <a
        href={`${previewLink}`}
        target="_blank"
        rel="noopener"
        data-vars-book-preview-event-label={asin}
      >
        {innerContent}
      </a>
    );

    return previewLink && asin ? (
      <>
        <a
          target="_blank"
          rel="noopener"
          href={`${previewLink}`}
          data-vars-book-preview-event-label={asin}
        >
          {bookImageHTML}
        </a>
        {renderPreview !== false && asin ? (
          <div className={className}>{previewLinkHTML}</div>
        ) : (
          ""
        )}
      </>
    ) : (
      <>
        {bookImageHTML}
        <div className={className}>No Preview</div>
      </>
    );
  };

  const operators = {
    $lte: (a, b) => a <= b,
    $lt: (a, b) => a < b,
    $gte: (a, b) => a >= b,
    $gt: (a, b) => a > b,
    $eq: (a, b) => a == b,
  };

  const shouldShowPromoBanner = (deal, bannerInfo) => {
    if (!deal?.promoPrice || !bannerInfo?.promoPrice) return false;

    const dealPrice = parseFloat(deal.promoPrice.replace("$", ""));
    const bannerPrice = parseFloat(bannerInfo.promoPrice.price);
    const operator = bannerInfo.promoPrice.operator;

    return operators[operator]
      ? operators[operator](dealPrice, bannerPrice)
      : false;
  };

  const retailClick = (gaName) => {
    let gaPayload = {
      eventCategory: "Ebook",
      eventLabel: `${deal.title} | ${deal.isbn} ${deal.publisher ? "|" + " " + deal.publisher : ""
        }`,
      eventAction: `Retail Click, ${gaName}`,
    };

    if (typeof window != "undefined" && window.dataLayer != undefined) {
      if (deal.isbn) {
        window.dataLayer.push({ event: gaPayload.eventAction, ...gaPayload });
      }
    }
  };

  return (
    <div className={styles.container}>
      <div key={index} className={styles.bookBoxBody}>
        <div
          className={`${styles.dFlex} ${styles.justifyContentSpaceBetween} ${styles.pricingBox}`}
        >
          <div className={styles.imgBox}>
            {renderBookImage(
              deal.title,
              deal.image,
              deal?.retailerLinks?.Amazon
            )}
          </div>
          <div
            className={`${styles.pricingBox} ${styles.dFlex} ${styles.justifyContentSpaceBetween}`}
          >
            <div className={styles.pricingDetail}>
              <div className={styles.mobileReviewSec}>
                <div>
                  <RatingStarDisplay rating={deal.latestRating} />
                  <span className={styles.reviewCount}>
                    {deal.latestReviews ? `${deal.latestReviews} reviews` : ""}
                  </span>
                </div>
                {bannerInfo?.text &&
                  bannerInfo?.color &&
                  shouldShowPromoBanner(deal, bannerInfo) ? (
                  <div
                    className={styles.promoBanner}
                    style={{ backgroundColor: bannerInfo.color }}
                  >
                    {bannerInfo.text}
                  </div>
                ) : renderComingSoonBanner ? (
                  <div>
                    <span>
                      <NextImage
                        ImageUrl={`${process.env.S3_IMAGE_URL}/New_to_EBB_Tag-Long.png`}
                        style={{ objectFit: "contain" }}
                        width={200}
                        height={24}
                        isLazyload={true}
                      />
                    </span>
                  </div>
                ) : displayFreeVoiceBanner ? (
                  <div>
                    <a
                      href="https://earlybirdbooks.com/free-voices"
                      target="_blank"
                      rel="noopener"
                    >
                      <NextImage
                        ImageUrl={`${process.env.S3_IMAGE_URL}/Free_Voices_Banner-Long.png`}
                        style={{ objectFit: "contain" }}
                        width={200}
                        height={24}
                        isLazyload={true}
                      />
                    </a>
                  </div>
                ) : isPromotedToKindleDeals ? (
                  <NextImage
                    ImageUrl={`${process.env.S3_IMAGE_URL}/Kindle_Unlimited-Tag.png`}
                    style={{ objectFit: "contain" }}
                    width={200}
                    height={24}
                    isLazyload={true}
                  />
                ) : null}
              </div>
              <div className={styles.titleText}>{deal.title}</div>
              <p className={styles.authorName}>by {deal.authors}</p>
              <div className={styles.mobileReviewSec}>
                <div className={styles.category}>
                  {deal?.category
                    ?.split(",") // Convert string to an array
                    ?.slice(0, 2) // Get only the first two items
                    .join(", ")}{" "}
                </div>
              </div>
              <div className={styles.desktopDescription}>
                <ReadMoreLess
                  styleName={styles.descriptionText}
                  text={deal.description}
                  textStyle={{ fontWeight: "bold" }}
                />
              </div>
              {postType !== Post.TYPES.KINDLE_UNLIMITED_DEALS && <div className={showRetailerForPrice ? styles.retailerPriceText : styles.promoPriceText}>
                {showRetailerForPrice ? (
                  "See Retailer for Price"
                ) : (
                  <>
                    {deal.promoPrice !== "$" && formatPrice(deal.promoPrice)}{" "}
                    <span
                      className={deal.promoPrice !== "$" ? styles.oldPrice : ""}
                    >
                      {formatPrice(deal.dlp)}
                    </span>
                  </>
                )}
              </div>}
            </div>
          </div>
          <div className={styles.reviewSec}>
            <div>
              <RatingStarDisplay rating={deal.latestRating} />
              <span className={styles.reviewCount}>
                {deal.latestReviews ? `${deal.latestReviews} reviews` : ""}
              </span>
            </div>
            {bannerInfo?.text &&
              bannerInfo?.color &&
              shouldShowPromoBanner(deal, bannerInfo) ? (
              <div
                className={styles.promoBanner}
                style={{ backgroundColor: bannerInfo.color }}
              >
                {bannerInfo.text}
              </div>
            ) : renderComingSoonBanner ? (
              <div>
                <span>
                  <NextImage
                    ImageUrl={`${process.env.S3_IMAGE_URL}/New_to_EBB_Tag-Long.png`}
                    width={220}
                    height={25}
                    isLazyload={true}
                  />
                </span>
              </div>
            ) : displayFreeVoiceBanner ? (
              <div>
                <a
                  href="https://earlybirdbooks.com/free-voices"
                  target="_blank"
                  rel="noopener"
                >
                  <NextImage
                    ImageUrl={`${process.env.S3_IMAGE_URL}/Free_Voices_Banner-Long.png`}
                    width={220}
                    height={25}
                    isLazyload={true}
                  />
                </a>
              </div>
            ) : isPromotedToKindleDeals ? (
              <NextImage
                ImageUrl={`${process.env.S3_IMAGE_URL}/Kindle_Unlimited-Tag.png`}
                width={220}
                height={25}
                isLazyload={true}
              />
            ) : null}
            <div className={styles.category}>
              {deal?.category
                ?.split(",") // Convert string to an array
                ?.slice(0, 2) // Get only the first two items
                .join(", ")}{" "}
            </div>
          </div>
        </div>
        <div className={styles.mobileDescription}>
          <ReadMoreLess
            styleName={styles.descriptionText}
            text={deal.description}
            textStyle={{ fontWeight: "bold" }}
          />
        </div>
        <div className={`${styles.dFlex} ${styles.siteListing}`}>
          {Object.entries(deal.retailerLinks || {})
            .filter(([site]) => {
              const normalizedSite = site.toLowerCase();
              // Exclude "BAM" retailer
              if (normalizedSite === "bam") return false;

              // If no retailers are selected, show all retailers
              if (!selectedRetailers || selectedRetailers.length === 0) {
                return true;
              }

              // Normalize site name for comparison (bookshop.org -> bookshop)
              let normalizedSiteForComparison = normalizedSite;
              if (normalizedSite === "bookshop.org") {
                normalizedSiteForComparison = "bookshop";
              }

              // Normalize selectedRetailers to consider 'itunes' as 'apple' and 'bookshop.org' as 'bookshop'
              const normalizedSelectedRetailers = selectedRetailers?.map((r) => {
                const lowerR = r.toLowerCase();
                if (lowerR === "itunes") return "apple";
                if (lowerR === "bookshop.org") return "bookshop";
                return lowerR;
              });

              // Check if the normalized site is in the selected retailers
              return normalizedSelectedRetailers.includes(normalizedSiteForComparison);
            })
            .map(([site, link], index) => {
              const retailerNames = {
                BN: "Barnes & Noble",
                BAM: "Books A Million",
                // Bookshop: "Bookshop",
                Itunes: "Apple",
              };

              return (
                <div key={index} className={styles.siteBox}>
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener"
                    onClick={() => retailClick(retailerNames[site] || site)}
                  >
                    {retailerNames[site] || site}{" "}
                  </a>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default BookDealsCardNew;
